import {isValidCcoaNum} from "../common/accounting";

export default class CcoaString {
  private readonly val: string;

  constructor(val: string) {
    if (!isValidCcoaNum(val))
      throw new Error(`Invalid CCOA string: ${val}`);
    this.val = val;
  }

  public static parse(val: string): CcoaString | undefined {
    return isValidCcoaNum(val) ? new CcoaString(val) : undefined;
  }

  public get entity(): string {
    return this.val.slice(0, 4);
  }

  public get fund(): string {
    return this.val.slice(5, 10);
  }

  public get fru(): string {
    return this.val.slice(11, 18);
  }

  public get account(): string {
    return this.val.slice(19, 25);
  }

  public get purpose(): string {
    return this.val.slice(26, 28);
  }

  public get program(): string {
    return this.val.length >= 31 ? this.val.slice(29, 32) : '000';
  }

  public get project(): string {
    return this.val.length >= 38 ? this.val.slice(33, 40) : '0000000';
  }

  public get activity(): string {
    return this.val.length >= 45 ? this.val.slice(41, 47) : '000000';
  }

  public get campusCommitment(): string {
    return this.val.length >= 52 ? this.val.slice(48, 53) : '00000';
  }

  toString(): string {
    // Build the minimal required string (entity.fund.fru.account.purpose)
    let result = `${this.entity}.${this.fund}.${this.fru}.${this.account}.${this.purpose}`;
    
    // Determine which fields need to be included based on later fields having values
    const needsCampusCommitment = this.campusCommitment !== '00000';
    const needsActivity = this.activity !== '000000' || needsCampusCommitment;
    const needsProject = this.project !== '0000000' || needsActivity;
    const needsProgram = this.program !== '000' || needsProject;
    
    // Add optional fields in sequence as needed
    if (needsProgram) result += `.${this.program}`;
    if (needsProject) result += `.${this.project}`;
    if (needsActivity) result += `.${this.activity}`;
    if (needsCampusCommitment) result += `.${this.campusCommitment}`;
    
    return result;
  }
 
  public static format(ccoa: string | undefined): string {
    const x = CcoaString.parse(ccoa ?? '');
    return x ? x.toString() : '';
  }
}
